// src/config/axiosInstance.ts
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { supabase } from './supabaseClient';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

const sanitizeApiUrlForTelemetry = (url?: string): string | undefined => {
  if (!url) {
    return undefined;
  }

  return url.split('?', 1)[0];
};

const shouldReportApiErrorToSentry = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return true;
  }

  const status = error.response?.status;

  if (status === 401) {
    return false;
  }

  // Evitamos reportar Network Error genéricos sin respuesta HTTP.
  // Son frecuentes por navegación, cortes de red, cancelaciones o bloqueos del navegador
  // y generan demasiado ruido en Sentry.
  if (!status) {
    return false;
  }

  return status >= 500;
};

const reportApiErrorToSentry = (error: unknown): void => {
  if (!shouldReportApiErrorToSentry(error)) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('proa.source', 'frontend_api');

    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
      const sanitizedUrl = sanitizeApiUrlForTelemetry(error.config?.url);

      scope.setTag('http.method', method);

      if (status) {
        scope.setTag('http.status_code', String(status));
      }

      if (sanitizedUrl) {
        scope.setTag('http.route_hint', sanitizedUrl);
        scope.setFingerprint([
          'frontend-api-error',
          method,
          sanitizedUrl,
          String(status ?? 'network'),
        ]);
      }

      scope.setContext('api_error', {
        method,
        url: sanitizedUrl,
        status,
        code: error.code,
        message: error.message,
      });
    }

    Sentry.captureException(error);
  });
};

// --- INTERCEPTOR DE PETICIONES: LA SOLUCIÓN DEFINITIVA A LA CONDICIÓN DE CARRERA ---
// Este interceptor se ejecuta ANTES de cada petición.
axiosInstance.interceptors.request.use(
  async (config) => {
    // 1. Obtiene la sesión MÁS RECIENTE de Supabase.
    const { data: { session } } = await supabase.auth.getSession();

    // 2. Si hay una sesión y un token, lo inyecta en la cabecera de ESTA petición.
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
    
    // 3. Devuelve la configuración modificada para que la petición continúe.
    return config;
  },
  (error) => {
    // Si hay un error al configurar la petición, lo rechaza.
    return Promise.reject(error);
  }
);

// ELIMINAMOS EL LISTENER onAuthStateChange. El interceptor de peticiones lo hace obsoleto y más seguro.

// Mantenemos el interceptor de respuestas para manejar errores globales.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error("AxiosInterceptor: Error 401. Sesión inválida. Disparando evento de purga de autenticación.");
      window.dispatchEvent(new CustomEvent('auth-purge'));
      return Promise.reject(error);
    }

    // PRC-018: el acceso a Pricing terminó a mitad de sesión. Solo este código
    // exacto; cualquier otro 403 (p. ej. de Industrial) sigue igual.
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail === 'PROAPRICING_SUBSCRIPTION_REQUIRED'
    ) {
      window.dispatchEvent(new CustomEvent('pricing-access-required'));
    }

    reportApiErrorToSentry(error);
    return Promise.reject(error);
  }
);

export default axiosInstance;